import { Heading, Paragraph } from '@zillow/constellation-10';
import type { EvictionRecord } from '@zg-rentals/ts-capps-client';
import {
  getCurrencyDisplay,
  getFormattedAddress,
  getFullNameForBackgroundReport,
} from '@zg-rentals/applications-utils';
import { formatDate } from '@zg-rentals/formatters';
import { capitalize } from 'lodash-es';
import { getTitleCase } from '../../../utils/formatters';

export function InlineDt({ children }: { children: React.ReactNode }) {
  return <dt style={{ display: 'inline' }}>{children}</dt>;
}

export function InlineDd({ children }: { children: React.ReactNode }) {
  return <dd style={{ display: 'inline', marginLeft: 'unset' }}>{children}</dd>;
}

export function EvictionSectionContent({ record }: { record: EvictionRecord }) {
  const {
    firstName,
    middleName,
    lastName,
    address,
    city,
    state,
    zipCode,
    caseFilingDate,
    caseNumber,
    noticeType,
    judgement,
    judgementDate,
    judgementAmount,
    judgementReleaseDate,
    restitutionOfPremises,
    plaintiff,
    plaintiffAttorney,
  } = record;
  const fullName = getFullNameForBackgroundReport({ firstName, middleName, lastName });
  const fullAddress = getFormattedAddress({ street: address, city, state, zip: zipCode });

  function getDispositionOfJudgment(code: string) {
    switch (code) {
      case 'RP':
        return 'Judgment with restitution of premise';
      case 'STIP':
        return 'Stipulated judgment';
      default:
        return getTitleCase(code);
    }
  }

  return (
    <>
      {fullName && <Paragraph textStyle="body-sm">Full name: {fullName}</Paragraph>}
      {fullAddress && <Paragraph textStyle="body-sm">Address: {fullAddress}</Paragraph>}

      {(caseFilingDate || caseNumber || noticeType) && (
        <>
          <Heading
            level={5}
            css={{ textStyle: 'body-xs-bold', color: 'text.subtle', marginTop: 'default', marginBottom: 'tighter' }}
          >
            Case details
          </Heading>
          <dl>
            {caseFilingDate && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Case filing date:</InlineDt> <InlineDd>{formatDate(caseFilingDate, 'numbers')}</InlineDd>
              </Paragraph>
            )}
            {caseNumber && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Case number:</InlineDt> <InlineDd>{caseNumber}</InlineDd>
              </Paragraph>
            )}
            {noticeType && (
              <Paragraph textStyle="body-sm">
                <InlineDt>Notice type:</InlineDt> <InlineDd>{capitalize(noticeType)}</InlineDd>
              </Paragraph>
            )}
          </dl>
        </>
      )}

      <Heading
        level={5}
        css={{ textStyle: 'body-xs-bold', color: 'text.subtle', marginTop: 'default', marginBottom: 'tighter' }}
      >
        Judgment details
      </Heading>
      <dl>
        {judgement && (
          <Paragraph textStyle="body-sm">
            <InlineDt>Judgment:</InlineDt> <InlineDd>{capitalize(judgement)}</InlineDd>
          </Paragraph>
        )}
        {judgementDate && (
          <Paragraph textStyle="body-sm">
            <InlineDt>Judgment date:</InlineDt> <InlineDd>{formatDate(judgementDate, 'numbers')}</InlineDd>
          </Paragraph>
        )}
        <Paragraph textStyle="body-sm">
          <InlineDt>Judgment amount:</InlineDt> <InlineDd>{getCurrencyDisplay(judgementAmount) || 'N/A'}</InlineDd>
        </Paragraph>
        {judgementReleaseDate && (
          <Paragraph textStyle="body-sm">
            <InlineDt>Judgment release date:</InlineDt>{' '}
            <InlineDd>{formatDate(judgementReleaseDate, 'numbers')}</InlineDd>
          </Paragraph>
        )}
        {restitutionOfPremises && (
          <Paragraph textStyle="body-sm">
            <InlineDt>Disposition of judgment:</InlineDt>{' '}
            <InlineDd>{getDispositionOfJudgment(restitutionOfPremises)}</InlineDd>
          </Paragraph>
        )}
      </dl>

      {(plaintiff || plaintiffAttorney) && (
        <>
          <Heading
            level={5}
            css={{ textStyle: 'body-xs-bold', color: 'text.subtle', marginTop: 'default', marginBottom: 'tighter' }}
          >
            Plaintiff details
          </Heading>
          {plaintiff && (
            <Paragraph textStyle="body-sm">
              <InlineDt>Plaintiff’s name:</InlineDt> <InlineDd>{getTitleCase(plaintiff)}</InlineDd>
            </Paragraph>
          )}
          {plaintiffAttorney && (
            <Paragraph textStyle="body-sm">
              <InlineDt>Plaintiff attorney:</InlineDt> <InlineDd>{getTitleCase(plaintiffAttorney)}</InlineDd>
            </Paragraph>
          )}
        </>
      )}
    </>
  );
}
